/* eslint-disable ember/no-computed-properties-in-native-classes, ember/require-computed-property-dependencies */
import Model, {
  attr,
  belongsTo,
  type AsyncBelongsTo,
} from '@warp-drive/legacy/model';
import { computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { isEmpty } from '@ember/utils';
import type { Type } from '@warp-drive/core/types/symbols';
import moment from 'moment';
import Block from 'linced-app/enums/medical/block';
import Administered from 'linced-app/enums/medical/log-administered';
import LogType from 'linced-app/enums/medical/log-type';
import Reason from 'linced-app/enums/medical/reason';
import type Batch from 'linced-app/models/medical/batch';
import type Medication from 'linced-app/models/medical/medication';
import type Schedule from 'linced-app/models/medical/schedule';
import type Person from 'linced-app/models/person';
import type School from 'linced-app/models/school';

// Proto: medical.Log.
export default class Log extends Model {
  declare [Type]: 'medical/log';

  // createTime is when the resource was created in seconds from the epoch.
  @attr('number')
  declare createTime: number | undefined;

  // updateTime is the last time the resource was modified in seconds from
  // the epoch.
  @attr('number')
  declare updateTime: number | undefined;

  // schoolId is the school the sickbay attendance record is assigned to.
  @belongsTo('school', { async: true, inverse: null })
  declare schoolId: AsyncBelongsTo<School>;

  @alias('schoolId')
  declare school: AsyncBelongsTo<School>;

  // logType is the type of log - usually administration, but can be a stock update
  @attr('number', { defaultValue: 0 })
  declare logType: number;

  // medicationId is the id of the medication for the administration log
  @belongsTo('medical/medication', { async: true, inverse: null })
  declare medicationId: AsyncBelongsTo<Medication>;

  @alias('medicationId')
  declare medication: AsyncBelongsTo<Medication>;

  @alias('medication.id')
  declare medication_id: string | null | undefined;

  // batchId is the id of the batch for the administration log
  @belongsTo('medical/batch', { async: true, inverse: null })
  declare batchId: AsyncBelongsTo<Batch>;

  @alias('batchId')
  declare batch: AsyncBelongsTo<Batch>;

  @alias('batch.id')
  declare batch_id: string | null | undefined;

  // displayDate is a timestamp used to display a date for the administration log
  @attr('number')
  declare displayDate: number | undefined;

  // block is the block the administration happened
  @attr('number', { defaultValue: 0 })
  declare block: number;

  // administeredBy is the type of person the medication was administered by
  @attr('number', { defaultValue: 0 })
  declare administeredBy: number;

  // administeredById is the id of the person the medication was administered by
  @belongsTo('person', { async: true, inverse: null })
  declare administeredById: AsyncBelongsTo<Person>;

  @alias('administeredById')
  declare administeredByPerson: AsyncBelongsTo<Person>;

  @alias('administeredByPerson.id')
  declare administeredByPerson_id: string | null | undefined;

  // administeredByString is the name of the person the medication was administered by
  @attr('string', { defaultValue: '' })
  declare administeredByString: string;

  // administeredTo is the type of person the medication was administered to
  @attr('number', { defaultValue: 0 })
  declare administeredTo: number;

  // administeredToId is the id of the person the medication was administered to
  @belongsTo('person', { async: true, inverse: null })
  declare administeredToId: AsyncBelongsTo<Person>;

  @alias('administeredToId')
  declare administeredToPerson: AsyncBelongsTo<Person>;

  @alias('administeredToPerson.id')
  declare administeredToPerson_id: string | null | undefined;

  // administeredToString is the name of the person the medication was administered to
  @attr('string', { defaultValue: '' })
  declare administeredToString: string;

  // dosage is dosage of the medication administration
  @attr('string', { defaultValue: '' })
  declare dosage: string;

  // scheduleId indicates the schedule the administration relates to
  @belongsTo('medical/schedule', { async: true, inverse: 'logs' })
  declare scheduleId: AsyncBelongsTo<Schedule>;

  @alias('scheduleId')
  declare schedule: AsyncBelongsTo<Schedule>;

  @alias('schedule.id')
  declare schedule_id: string | null | undefined;

  // administrationDetails is the details of the medication administration
  @attr('string', { defaultValue: '' })
  declare administrationDetails: string;

  // reason is the reason for the medication/batch update
  @attr('number', { defaultValue: 0 })
  declare reason: number;

  // replaced indicates if the medication was replaced or not
  @attr('boolean', { defaultValue: false })
  declare replaced: boolean;

  // notifyCaregiver indicates that the caregiver is to be notified
  // of this administration
  @attr('boolean', { defaultValue: false })
  declare notifyCaregiver: boolean;

  // deleted is used to implement soft-deletes
  @attr('boolean', { defaultValue: false })
  declare deleted: boolean;

  @computed('logType')
  get logTypeString() {
    if (this.logType) {
      return LogType.getLabel(this.logType);
    }
    return '';
  }

  @computed('notifyCaregiver')
  get notifyCaregiverString() {
    return this.notifyCaregiver ? 'Yes' : 'No';
  }

  @computed('displayDate')
  get displayDateString() {
    return this.displayDate
      ? moment.unix(this.displayDate).format('YYYY-MM-DD HH:mm')
      : '';
  }

  @computed('block')
  get blockString() {
    if (this.block) {
      return Block.getLabel(this.block);
    }
    return '';
  }

  @computed('administeredBy')
  get administeredByTypeString() {
    if (this.administeredBy) {
      return Administered.getLabel(this.administeredBy);
    }
    return '';
  }

  @computed('administeredByPerson', 'administeredByString')
  get administeredByName() {
    return (
      this.administeredByPerson.get('fullName') || this.administeredByString
    );
  }

  @computed('administeredTo')
  get administeredToTypeString() {
    if (this.administeredTo) {
      return Administered.getLabel(this.administeredTo);
    }
    return '';
  }

  @computed('administeredToPerson', 'administeredToString')
  get administeredToName() {
    return (
      this.administeredToPerson.get('fullName') || this.administeredToString
    );
  }

  @computed('schedule_id')
  get scheduledString() {
    if (this.logType === LogType.UPDATE) {
      return '';
    }
    return this.schedule_id ? 'Yes' : 'No';
  }

  @computed('reason')
  get reasonString() {
    if (this.reason) {
      return Reason.getLabel(this.reason);
    }
    return '';
  }

  @computed(
    'medication_id',
    'batch_id',
    'displayDate',
    'block',
    'administeredByPerson_id',
    'administeredByString',
    'administeredToPerson_id',
    'administeredToString',
    'dosage',
    'administrationDetails',
    'reason',
    'replaced',
  )
  get isValid() {
    if (isEmpty(this.medication_id)) {
      return false;
    }
    if (isEmpty(this.batch_id)) {
      return false;
    }
    if (isEmpty(this.displayDate)) {
      return false;
    }
    if (!this.block) {
      return false;
    }
    if (
      isEmpty(this.administeredByPerson_id) &&
      isEmpty(this.administeredByString)
    ) {
      return false;
    }
    if (
      isEmpty(this.administeredToPerson_id) &&
      isEmpty(this.administeredToString)
    ) {
      return false;
    }
    if (isEmpty(this.dosage)) {
      return false;
    }

    return true;
  }
}
